import { assert, VoidCallback } from '@deqo/foundation';
import { darken } from 'polished';
import React, { ReactNode, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { LFC } from '../Legacy';
import { Colors } from './Colors';
import { HeroIconKey, HeroIcons } from './HeroIcons';
import { Roundings, Shadows } from './Standards';
import { tapOrClick } from './TapOrClick';

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'square';
const buttonSizeStyles = {
    xs: css`
        padding: .25rem .5rem;
        font-size: .75rem;
        line-height: 1rem;
    `,
    sm: css`
        padding: .375rem .625rem;
        font-size: .8125rem;
        line-height: 1rem;
    `,
    md: css`
        padding: .5rem .75rem;
        font-size: .875rem;
        line-height: 1rem;
    `,
    lg: css`
        padding: .5rem 1rem;
        font-size: .875rem;
        line-height: 1.25rem;
    `,
    xl: css`
        padding: .5rem 1rem;
        font-size: 1rem;
        line-height: 1.5rem;
    `,
    xxl: css`
        padding: .75rem 1.5rem;
        font-size: 1rem;
        line-height: 1.5rem;
    `,
    square: css`
        padding: .5rem .5rem;
        font-size: .875rem;
        line-height: 1rem;
    `,
};

export type ButtonVariant = 'primary' | 'positive' | 'negative' | 'mystery' | 'observe' | 'common' | 'frame' | 'text';
const buttonVariantStyles = {
    primary: css`
        color: white; background: ${Colors.primary};
        &:hover { background: ${darken(0.05, Colors.primary)} }
        &:focus { background: ${darken(0.05, Colors.primary)} }
        &:active { background: ${darken(0.10, Colors.primary)} }
    `,
    positive: css`
        color: white; background: ${Colors.positive};
        &:hover { background: ${darken(0.05, Colors.positive)} }
        &:focus { background: ${darken(0.05, Colors.positive)} }
        &:active { background: ${darken(0.10, Colors.positive)} }
    `,
    negative: css`
        color: white; background: ${Colors.negative};
        &:hover { background: ${darken(0.15, Colors.negative)} }
        &:focus { background: ${darken(0.15, Colors.negative)} }
        &:active { background: ${darken(0.20, Colors.negative)} }
    `,
    mystery: css`
        color: white; background: ${Colors.mystery};
        &:hover { background: ${darken(0.075, Colors.mystery)} }
        &:focus { background: ${darken(0.075, Colors.mystery)} }
        &:active { background: ${darken(0.125, Colors.mystery)} }
    `,
    observe: css`
        color: white; background: ${Colors.observe};
        &:hover { background: ${darken(0.075, Colors.observe)} }
        &:focus { background: ${darken(0.075, Colors.observe)} }
        &:active { background: ${darken(0.125, Colors.observe)} }
    `,
    common: css`
        color: ${Colors.text};
        background: white;
        border-color: ${Colors.gray300};
        &:hover { background: ${darken(0.025, '#fff')} }
        &:focus { border-color: ${Colors.blue300} }
        &:active { background: ${darken(0.05, '#fff')} }
    `,
    frame: css`
        color: ${Colors.text};
        background: transparent;
        border-color: ${Colors.gray300};
        &:hover { background: ${Colors.hover} }
        &:focus { border-color: ${Colors.blue300} }
        &:active { background: ${Colors.active} }
    `,
    text: css`
        color: inherit;
        background: transparent;
        border-color: transparent;
        &:hover { background: rgba(0,0,0,0.1) }
        &:active { background: rgba(0,0,0,0.2) }
    `,
};

export type ButtonGroupPosition = 'start' | 'middle' | 'end';
const buttonGroupPositionStyles = {
    start: css`
        border-top-right-radius: 0; 
        border-bottom-right-radius: 0;
    `,
    middle: css`
        border-radius: 0; 
        margin-left: -1px;
    `,
    end: css`
        border-top-left-radius: 0; 
        border-bottom-left-radius: 0;
    `,
};

const Frame = styled.div<{ block?: boolean, disabled?: boolean, variant: ButtonProps['variant'] }>`
    display: ${p => p.block ? 'flex' : 'inline-flex'};
    box-shadow: ${p => p.variant === 'text' ? 'none' : Shadows.sm};
    border-radius: ${Roundings.sm};

    ${p => !p.disabled ? '' : css`
        opacity: 0.5;
        pointer-events: none;
    `}
`;

//  active:bg-indigo-700 transition

const Inner = styled.div<{ block?: boolean, group?: ButtonGroupPosition, size?: ButtonSize, variant?: ButtonVariant }>`
    display: ${p => p.block ? 'flex' : 'inline-flex'};
    max-width: 100%;
    align-items: center;
    justify-content: space-between;
    flex: 1 1 auto;
    gap: .25rem;
    border-radius: .25rem;
    border: solid 0.0625rem transparent;
    font-weight: 450;
    cursor: pointer;
    user-select: none;

    ${p => buttonSizeStyles[p.size || 'lg']}
    ${p => buttonVariantStyles[p.variant || 'common']}
    ${p => !!p.group ? buttonGroupPositionStyles[p.group] : ''}

    &:focus {
        outline: none;
    }

    &:active {
        outline: none;
    }
`;

const Icon = styled.span`
    display: inline-flex;
    margin-left: -0.25rem;
    margin-right: 0.5rem;
`;

export type ButtonProps = {
    block?: boolean,
    label?: string,
    icon?: (() => JSX.Element) | HeroIconKey,
    size?: ButtonSize,
    group?: ButtonGroupPosition,
    variant?: ButtonVariant,
    actionId?: string,
    disabled?: boolean,
    onAction?: VoidCallback,
    children?: ReactNode,
};

const Base: LFC<ButtonProps> = props => {

    assert(!(props.icon || props.label) || !props.children,
        'Button: use children OR label/icon prop.');

    const { onAction, block, variant, size, group, icon, label, disabled, children, actionId } = props;
    const iconElmt = resolveIcon(icon) as any;
    const invokeAction = useCallback(() => void (!disabled && onAction ? onAction() : null), [disabled, onAction]);

    return <Frame data-action={actionId} block={block} variant={variant} disabled={disabled} {...tapOrClick(invokeAction)}>
        <Inner block={block} variant={variant} size={size} group={group} >
            {icon && <Icon>{iconElmt()}</Icon>}
            {label}
            {children}
        </Inner>
    </Frame>;
};

export const Button = Object.assign(Base, { Frame, Inner });

const resolveIcon = (icon?: (Pick<ButtonProps, 'icon'>)['icon']) => {
    if (typeof (icon) === 'string') return () => React.createElement(HeroIcons[icon], { size: '1.5em' });
    return icon;
};
